import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { AlertTriangle, Check, CreditCard } from "lucide-react";

const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

export default function PostPaymentSignup() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paymentEmail, setPaymentEmail] = useState<string | null>(null);
  const [initialEmail, setInitialEmail] = useState('');
  
  // Get query parameters
  const params = new URLSearchParams(window.location.search);
  const paymentIntentId = params.get('payment_intent');
  const paymentIntentClientSecret = params.get('payment_intent_client_secret');
  const redirectStatus = params.get('redirect_status');
  
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: initialEmail,
      password: "",
      confirmPassword: "",
    },
  });
  
  // Set up email value when it's available
  useEffect(() => {
    if (initialEmail) {
      form.setValue('email', initialEmail);
    }
  }, [initialEmail, form]);
  
  useEffect(() => {
    // Verify payment was successful
    const verifyPayment = async () => {
      if (paymentIntentId) {
        try {
          // Extract customer email from session data or query payment intent
          // This would typically come from your backend after verifying the payment
          const customerEmail = params.get('customer_email') || '';
          console.log("Verifying payment with email:", customerEmail);
          
          // Store payment data in session for registration
          const response = await apiRequest('POST', '/api/store-payment-data', {
            paymentIntentId,
            customerEmail,
            sessionId: document.cookie.match(/connect\.sid=([^;]+)/)?.[1] || ''
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log("Payment verification response:", data);
            if (data.success) {
              setPaymentVerified(true);
              setPaymentEmail(customerEmail);
              
              // Update initial email for form
              if (customerEmail) {
                setInitialEmail(customerEmail);
              }
            } else {
              setSignupError(`Payment verification issue: ${data.message || 'Unknown error'}`);
            }
          } else {
            console.error("Payment verification failed response:", response);
            let errorMessage = 'Payment verification failed';
            try {
              const errorData = await response.json();
              errorMessage += `: ${errorData.message || ''}`;
            } catch (e) {
              // If we can't parse the JSON, just use the status
              errorMessage += ` (Status: ${response.status})`;
            }
            setSignupError(errorMessage);
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          setSignupError('Could not verify payment. Please contact support.');
        }
      } else if (redirectStatus === 'failed') {
        setSignupError('Payment was unsuccessful. Please try again.');
      } else if (!paymentIntentId) {
        setSignupError('No payment information found. You must complete payment before creating an account.');
      }
    };
    
    verifyPayment();
  }, [paymentIntentId, redirectStatus, params]);

  const signupMutation = useMutation({
    mutationFn: async (data: Omit<SignupFormValues, "confirmPassword">) => {
      const response = await apiRequest("POST", "/api/auth/register-paid", data);
      const jsonData = await response.json();
      
      if (!response.ok) {
        throw new Error(jsonData.message || "Registration failed");
      }
      
      return jsonData;
    },
    onSuccess: () => {
      toast({
        title: "Account created",
        description: "Your account has been successfully created with premium access!",
      });
      setSignupSuccess(true);
      
      // Redirect to premium content after a short delay
      setTimeout(() => {
        setLocation("/premium");
      }, 3000);
    },
    onError: (error: any) => {
      console.error("Signup error:", error);
      
      if (error.message.includes('Payment verification')) {
        setSignupError('Your payment must be completed before creating an account. Please subscribe first.');
      } else if (error.message.includes('email must match')) {
        setSignupError('Registration email must match the email used during payment.');
      } else {
        setSignupError(error.message || "Registration failed. Please try again.");
      }
    },
  });

  function onSubmit(data: SignupFormValues) {
    setSignupError(null);
    // Remove confirmPassword before submitting
    const { confirmPassword, ...submitData } = data;
    signupMutation.mutate(submitData);
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center bg-gray-50 py-12">
        <div className="w-full max-w-md px-4">
          <Card className="w-full shadow-lg border-0">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">Complete Your Registration</CardTitle>
              <CardDescription className="text-center">
                Create your account to access premium content
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {paymentVerified && (
                <Alert className="mb-4 bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Payment verified successfully! Complete your account setup below.
                  </AlertDescription>
                </Alert>
              )}
              
              {!paymentVerified && !signupError && (
                <Alert className="mb-4 bg-blue-50 border-blue-200">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Verifying your payment. If this takes more than a few seconds, please contact support.
                  </AlertDescription>
                </Alert>
              )}
              
              {signupError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{signupError}</AlertDescription>
                </Alert>
              )}
              
              {signupSuccess ? (
                <Alert className="mb-4 bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Your account has been successfully created. Redirecting to premium content...
                  </AlertDescription>
                </Alert>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="your@email.com" 
                              {...field} 
                              // Always allow email editing
                              disabled={false}
                            />
                          </FormControl>
                          {paymentEmail && (
                            <p className="text-xs text-gray-500">
                              This email must match the one used during payment.
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      disabled={signupMutation.isPending || !paymentVerified}
                    >
                      {signupMutation.isPending ? "Creating account..." : "Create account"}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4">
              <div className="text-sm text-center text-gray-500">
                Need help?{" "}
                <a href="mailto:support@example.com" className="text-blue-600 hover:text-blue-800 font-medium">
                  Contact support
                </a>
              </div>
              <div className="text-xs text-center text-gray-500">
                By creating an account, you agree to our Terms of Service and Privacy Policy
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}